// セクションの表示順・表示対象・対応パネル解決をまとめる resolver です。
// Kakousiji.js から状態参照だけを deps で受け取り、純粋に判定処理へ寄せます。
export function createSectionPanelResolver(deps) {
    // panel 配列内での表示順インデックスを返します。
    function sectionPanelOrderIndex(section) {
        const panel = deps.getPanel();
        if (deps.isEmpty(section) || deps.isEmpty(panel)) return Number.MAX_SAFE_INTEGER;

        const pid = String(deps.DicVal(section, "panelId", ""));
        const ptype = String(deps.DicVal(section, "panelType", ""));

        if (!deps.isEmpty(pid) && !deps.isEmpty(ptype)) {
            const idx = panel.findIndex((p) => String(p.id) === pid && String(p.type) === ptype);
            if (idx !== -1) return idx;
        }
        if (!deps.isEmpty(ptype)) {
            const idx = panel.findIndex((p) => String(p.type) === ptype);
            if (idx !== -1) return idx;
        }
        if (!deps.isEmpty(pid)) {
            const idx = panel.findIndex((p) => String(p.id) === pid);
            if (idx !== -1) return idx;
        }
        return Number.MAX_SAFE_INTEGER;
    }

    // panel 表示順を基準にセクション一覧を安定ソートします。
    function orderSectionsByPanel(defs) {
        if (!Array.isArray(defs)) return [];

        return defs
            .map((section, idx) => ({ section, idx, ord: sectionPanelOrderIndex(section) }))
            .sort((a, b) => {
                // 同順位のときは元の定義順を保持します。
                if (a.ord === b.ord) return a.idx - b.idx;
                if (a.ord === Number.MAX_SAFE_INTEGER) return 1;
                if (b.ord === Number.MAX_SAFE_INTEGER) return -1;
                return a.ord - b.ord;
            })
            .map((v) => v.section);
    }

    // 現在の加工指示区分とタブに応じた表示セクションを返します。
    function showSections() {
        if (deps.getKakouShijiKubun() === "sample") {
            return orderSectionsByPanel(deps.getCurrentSampleSectionDefs());
        }
        return orderSectionsByPanel(
            deps.getCurrentNormalSectionDefs().filter((section) => section.view === deps.getKakouType())
        );
    }

    // Entry Form 表示時は、受注/納品データの有無に関わらず基本情報タブから開始します。
    // (以前は実データのある受注/納品タブを優先していたが、常に基本情報を初期表示する仕様に変更)
    function resolveInitialDemandKakouType() {
        if (deps.getKakouType() === "type1") return "";
        if (deps.getKakouShijiKubun() === "sample") return "";
        return "type1";
    }

    // 商品情報系で使う代表 BASE パネルを返します。
    function getPrimaryBasePanel() {
        const panel = deps.getPanel();
        if (deps.isEmpty(panel)) return null;

        const basePanels = panel.filter((p) => p.id === deps.G_id.BASE);
        if (basePanels.length === 0) return null;

        const kubunColId = deps.getKakouShijiKubunColumnId();
        const columninfo = deps.getColumnInfo();
        if (!deps.isEmpty(kubunColId) && !deps.isEmpty(columninfo)) {
            const col = columninfo.find((c) => c.columnid === kubunColId);
            if (!deps.isEmpty(col)) {
                const typed = basePanels.find((p) => p.type === col.type);
                if (!deps.isEmpty(typed)) return typed;
            }
        }

        return basePanels[0];
    }

    // BASE セクションの対応 panel を解決します。
    function resolveSectionPanel(section) {
        const panel = deps.getPanel();
        if (section.component !== "base") return null;

        if (!deps.isEmpty(section.panelType) && !deps.isEmpty(panel)) {
            // BASE パネルは id=0 が重複するため、panelType を優先して解決します。
            const typedBase = panel.find(
                (p) =>
                    String(p.id ?? "") === deps.G_id.BASE &&
                    deps.canonicalPanelType(deps.DicVal(p, "type", "")) === deps.canonicalPanelType(section.panelType)
            );
            if (!deps.isEmpty(typedBase)) return typedBase;
        }

        if (section.id === "fukushizai") {
            const fukushizaiGridPanel = deps.resolveFukushizaiGridPanel();
            if (!deps.isEmpty(fukushizaiGridPanel)) {
                return { ...fukushizaiGridPanel, id: deps.G_id.BASE, grid: false };
            }
            if (!deps.isEmpty(section.panelType)) {
                return { id: deps.G_id.BASE, type: section.panelType, grid: false };
            }
        }

        if (section.id === "product" || section.id === "sample_base") return getPrimaryBasePanel();
        if (section.id === "base1" || section.id === "base2") return null;

        return null;
    }

    // コメントセクションの対応 panel を解決します。
    function resolveRemarksSectionPanel(section) {
        const panel = deps.getPanel();
        if (section.component !== "nb") return null;
        if (deps.isEmpty(panel)) return null;

        if (!deps.isEmpty(section.panelType)) {
            const typedRemarks = panel.find((p) => p.id === deps.G_id.NB && p.type === section.panelType);
            if (!deps.isEmpty(typedRemarks)) return typedRemarks;
        }
        if (!deps.isEmpty(section.panelId)) {
            const byId = panel.find((p) => p.id === section.panelId);
            if (!deps.isEmpty(byId)) return byId;
        }

        const firstRemarks = panel.find((p) => p.id === deps.G_id.NB);
        if (!deps.isEmpty(firstRemarks)) return firstRemarks;

        return null;
    }

    // GRID セクションの対応 panel を解決します。
    function resolveGridSectionPanel(section) {
        const panel = deps.getPanel();
        if (section.component !== "grid") return null;
        if (deps.isEmpty(panel)) return null;

        if (!deps.isEmpty(section.panelId) && !deps.isEmpty(section.panelType)) {
            const byIdType = panel.find(
                (p) =>
                    p.grid &&
                    String(p.id ?? "") === String(section.panelId ?? "") &&
                    String(p.type ?? "") === String(section.panelType ?? "")
            );
            if (!deps.isEmpty(byIdType)) return byIdType;
        }
        if (!deps.isEmpty(section.panelType)) {
            const byType = panel.find((p) => p.grid && String(p.type ?? "") === String(section.panelType ?? ""));
            if (!deps.isEmpty(byType)) return byType;
        }
        if (!deps.isEmpty(section.panelId)) {
            const byId = panel.find((p) => p.grid && String(p.id ?? "") === String(section.panelId ?? ""));
            if (!deps.isEmpty(byId)) return byId;
        }

        const target = panel.find((p) => p.grid && String(p.id ?? "") === String(section.id ?? ""));
        if (deps.isEmpty(target)) return null;

        return target;
    }

    // セクションの合計 type を返します。
    function sectionTotalType(section, gridPanel) {
        // パネル定義(totaltype)を優先します。
        if (!deps.isEmpty(gridPanel) && !deps.isEmpty(gridPanel.totaltype)) return gridPanel.totaltype;
        if (!deps.isEmpty(section.totalType)) return section.totalType;
        return "";
    }

    // 列定義に存在する合計 type へ正規化します。
    function normalizeTotalTypeForColumns(totaltype, sectionId) {
        const columninfo = deps.getColumnInfo();
        if (deps.isEmpty(totaltype)) return "";

        const hasType = (t) => !deps.isEmpty(columninfo) && columninfo.some((c) => String(c.type) === String(t));
        if (hasType(totaltype)) return String(totaltype);

        const candidates = [String(totaltype)];
        if (sectionId === "genka") candidates.push(deps.PANEL_TYPE.GENKA_SUMMARY);
        if (sectionId === "sijisu") candidates.push(deps.PANEL_TYPE.SIJISU_TOTAL);
        if (sectionId === "sample_info") candidates.push(deps.PANEL_TYPE.SAMPLE_TOTAL);

        const next = candidates.find((t) => hasType(t));
        return deps.isEmpty(next) ? String(totaltype) : String(next);
    }

    // detail から合計行を取得します。
    function pickTotalRows(detail, totaltype, sectionId) {
        if (deps.isEmpty(detail) || deps.isEmpty(totaltype)) return null;

        const candidates = [String(totaltype)];
        if (sectionId === "genka") candidates.push(deps.PANEL_TYPE.GENKA_SUMMARY);
        if (sectionId === "sijisu") candidates.push(deps.PANEL_TYPE.SIJISU_TOTAL);
        if (sectionId === "sample_info") candidates.push(deps.PANEL_TYPE.SAMPLE_TOTAL);

        const hit = candidates.find((k) => !deps.isEmpty(deps.DicVal(detail, k, null)));
        if (deps.isEmpty(hit)) return null;

        return deps.DicVal(detail, hit, null);
    }

    // 合計グリッドを載せる panel を解決します。
    function resolveTotalHostPanel(section, gridPanel) {
        const panel = deps.getPanel();
        if (!deps.isEmpty(gridPanel)) return gridPanel;

        const totaltype = sectionTotalType(section, gridPanel);
        if (deps.isEmpty(totaltype) || deps.isEmpty(panel)) return null;

        const typedPanel = panel.find((p) => String(p.type ?? "") === String(totaltype ?? ""));
        if (!deps.isEmpty(typedPanel)) return typedPanel;

        return null;
    }

    return {
        sectionPanelOrderIndex,
        orderSectionsByPanel,
        showSections,
        resolveInitialDemandKakouType,
        getPrimaryBasePanel,
        resolveSectionPanel,
        resolveRemarksSectionPanel,
        resolveGridSectionPanel,
        sectionTotalType,
        normalizeTotalTypeForColumns,
        pickTotalRows,
        resolveTotalHostPanel
    };
}
